"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import NextImage from "next/image";
import QRCode from "qrcode";

type ContentType = "url" | "text" | "wifi" | "vcard";
type WifiSecurity = "WPA" | "WEP" | "nopass";
type ErrorCorrection = "L" | "M" | "Q" | "H";

const DEBOUNCE_MS = 350;

/** Escapes characters that are special in the WiFi/vCard QR payload spec. */
function escapePayloadField(value: string) {
  return value.replace(/([\\;,:"])/g, "\\$1");
}

function buildWifiPayload(ssid: string, password: string, security: WifiSecurity, hidden: boolean) {
  const parts = [
    `T:${security}`,
    `S:${escapePayloadField(ssid)}`,
    security === "nopass" ? "" : `P:${escapePayloadField(password)}`,
    hidden ? "H:true" : "",
  ].filter(Boolean);
  return `WIFI:${parts.join(";")};;`;
}

function buildVCardPayload(fields: {
  name: string;
  org: string;
  phone: string;
  email: string;
  url: string;
}) {
  const lines = [
    "BEGIN:VCARD",
    "VERSION:3.0",
    fields.name ? `N:${escapePayloadField(fields.name)}` : "",
    fields.name ? `FN:${escapePayloadField(fields.name)}` : "",
    fields.org ? `ORG:${escapePayloadField(fields.org)}` : "",
    fields.phone ? `TEL:${escapePayloadField(fields.phone)}` : "",
    fields.email ? `EMAIL:${escapePayloadField(fields.email)}` : "",
    fields.url ? `URL:${escapePayloadField(fields.url)}` : "",
    "END:VCARD",
  ].filter(Boolean);
  return lines.join("\n");
}

/** Relative luminance-based check so we can warn on low-contrast colors. */
function luminance(hex: string): number {
  const value = hex.replace("#", "");
  const [r, g, b] = [0, 2, 4].map((offset) => Number.parseInt(value.slice(offset, offset + 2), 16) / 255);
  const [red, green, blue] = [r, g, b].map((c) => (c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4));
  return 0.2126 * red + 0.7152 * green + 0.0722 * blue;
}

function contrastRatio(a: string, b: string): number {
  const l1 = luminance(a);
  const l2 = luminance(b);
  return (Math.max(l1, l2) + 0.05) / (Math.min(l1, l2) + 0.05);
}

export default function QrCodeGenerator({}: { toolName: string }) {
  const debounceTimer = useRef<number | undefined>(undefined);
  const logoFileInputRef = useRef<HTMLInputElement>(null);
  const logoDataUrlRef = useRef(""); // revoked-free, since it's a data URL not an object URL

  const [type, setType] = useState<ContentType>("url");

  // URL / text
  const [simpleContent, setSimpleContent] = useState("https://aiutility.shop");

  // WiFi
  const [wifiSsid, setWifiSsid] = useState("");
  const [wifiPassword, setWifiPassword] = useState("");
  const [wifiSecurity, setWifiSecurity] = useState<WifiSecurity>("WPA");
  const [wifiHidden, setWifiHidden] = useState(false);

  // vCard
  const [vcardName, setVcardName] = useState("");
  const [vcardOrg, setVcardOrg] = useState("");
  const [vcardPhone, setVcardPhone] = useState("");
  const [vcardEmail, setVcardEmail] = useState("");
  const [vcardUrl, setVcardUrl] = useState("");

  // Appearance
  const [size, setSize] = useState(240);
  const [errorCorrection, setErrorCorrection] = useState<ErrorCorrection>("M");
  const [fgColor, setFgColor] = useState("#0f172a");
  const [bgColor, setBgColor] = useState("#ffffff");
  const [logoDataUrl, setLogoDataUrl] = useState("");
  const [logoName, setLogoName] = useState("");

  const [previewUrl, setPreviewUrl] = useState("");
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");
  const [isRendering, setIsRendering] = useState(false);

  const payload = useMemo(() => {
    switch (type) {
      case "wifi":
        return wifiSsid.trim() ? buildWifiPayload(wifiSsid, wifiPassword, wifiSecurity, wifiHidden) : "";
      case "vcard":
        return vcardName.trim() || vcardPhone.trim() || vcardEmail.trim()
          ? buildVCardPayload({
              name: vcardName,
              org: vcardOrg,
              phone: vcardPhone,
              email: vcardEmail,
              url: vcardUrl,
            })
          : "";
      default:
        return simpleContent.trim();
    }
  }, [type, simpleContent, wifiSsid, wifiPassword, wifiSecurity, wifiHidden, vcardName, vcardOrg, vcardPhone, vcardEmail, vcardUrl]);

  const contrast = contrastRatio(fgColor, bgColor);
  const lowContrast = contrast < 3; // QR codes need strong contrast to scan reliably
  const hasLogo = Boolean(logoDataUrl);
  const logoRiskyWithCorrection = hasLogo && errorCorrection === "L";

  const buildCanvas = useCallback(
    async (content: string) => {
      const canvas = document.createElement("canvas");
      canvas.width = size;
      canvas.height = size;

      await QRCode.toCanvas(canvas, content, {
        width: size,
        margin: 1,
        color: { dark: fgColor, light: bgColor },
        errorCorrectionLevel: errorCorrection,
      });

      if (logoDataUrl) {
        const logo = await new Promise<HTMLImageElement>((resolve, reject) => {
          const image = new window.Image();
          image.onload = () => resolve(image);
          image.onerror = () => reject(new Error("The logo image couldn't be loaded."));
          image.src = logoDataUrl;
        });

        const context = canvas.getContext("2d");
        if (!context) throw new Error("Unable to access the canvas context.");

        const logoSize = Math.max(40, Math.round(size * 0.22));
        const x = (size - logoSize) / 2;
        const y = (size - logoSize) / 2;
        context.save();
        context.fillStyle = bgColor;
        context.fillRect(x - 6, y - 6, logoSize + 12, logoSize + 12);
        context.beginPath();
        context.roundRect(x - 4, y - 4, logoSize + 8, logoSize + 8, 10);
        context.clip();
        context.drawImage(logo, x, y, logoSize, logoSize);
        context.restore();
      }

      return canvas;
    },
    [size, fgColor, bgColor, errorCorrection, logoDataUrl],
  );

  const renderPreview = useCallback(async () => {
    if (!payload) {
      setPreviewUrl("");
      setStatus(placeholderStatus(type));
      setError("");
      return;
    }

    setIsRendering(true);
    setError("");
    try {
      const canvas = await buildCanvas(payload);
      setPreviewUrl(canvas.toDataURL("image/png"));
      setStatus("Preview updated.");
    } catch (renderError) {
      setPreviewUrl("");
      setError(renderError instanceof Error ? renderError.message : "Unable to render this QR code.");
    } finally {
      setIsRendering(false);
    }
  }, [payload, buildCanvas, type]);

  // Debounced auto-render whenever meaningful inputs change.
  useEffect(() => {
    window.clearTimeout(debounceTimer.current);
    debounceTimer.current = window.setTimeout(() => {
      void renderPreview();
    }, DEBOUNCE_MS);
    return () => window.clearTimeout(debounceTimer.current);
  }, [renderPreview]);

  function handleLogoFile(file: File) {
    if (!file.type.startsWith("image/")) {
      setError("Please choose an image file for the logo.");
      return;
    }
    if (file.size > 3 * 1024 * 1024) {
      setError("The logo image should be under 3 MB.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const result = typeof reader.result === "string" ? reader.result : "";
      logoDataUrlRef.current = result;
      setLogoDataUrl(result);
      setLogoName(file.name);
      setError("");
      if (errorCorrection === "L") setErrorCorrection("Q");
    };
    reader.onerror = () => setError("That logo image couldn't be read.");
    reader.readAsDataURL(file);
  }

  function removeLogo() {
    logoDataUrlRef.current = "";
    setLogoDataUrl("");
    setLogoName("");
  }

  async function download(format: "png" | "svg") {
    if (!payload) {
      setStatus(placeholderStatus(type));
      return;
    }

    try {
      const filename = `qr-${type}-${Date.now()}.${format}`;
      const link = document.createElement("a");

      if (format === "png") {
        const canvas = await buildCanvas(payload);
        link.href = canvas.toDataURL("image/png");
      } else {
        const svgText = await QRCode.toString(payload, {
          type: "svg",
          width: size,
          margin: 1,
          color: { dark: fgColor, light: bgColor },
          errorCorrectionLevel: errorCorrection,
        });
        const blob = new Blob([svgText], { type: "image/svg+xml" });
        link.href = URL.createObjectURL(blob);
      }

      link.download = filename;
      link.click();
      if (format === "svg") window.setTimeout(() => URL.revokeObjectURL(link.href), 3000);
      setStatus(`Downloaded ${filename}.`);
    } catch (downloadError) {
      setError(downloadError instanceof Error ? downloadError.message : "Couldn't generate the download.");
    }
  }

  const pill = (active: boolean) =>
    `rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${
      active ? "bg-indigo-600 text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"
    }`;

  return (
    <div className="space-y-4">
      <div>
        <p className="text-sm font-medium text-slate-700">Content type</p>
        <div className="mt-2 flex flex-wrap gap-2" role="group" aria-label="Content type">
          {(["url", "text", "wifi", "vcard"] as ContentType[]).map((value) => (
            <button
              key={value}
              type="button"
              aria-pressed={type === value}
              onClick={() => setType(value)}
              className={pill(type === value)}
            >
              {value === "url" ? "URL" : value === "text" ? "Text" : value === "wifi" ? "WiFi" : "vCard"}
            </button>
          ))}
        </div>
      </div>

      {(type === "url" || type === "text") && (
        <label className="block text-sm font-medium text-slate-700">
          {type === "url" ? "URL" : "Text"}
          <textarea
            value={simpleContent}
            onChange={(event) => setSimpleContent(event.target.value)}
            rows={4}
            className="mt-2 w-full rounded-xl border border-slate-300 p-3 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200"
            placeholder={type === "url" ? "https://example.com" : "Any text you want to encode"}
          />
        </label>
      )}

      {type === "wifi" && (
        <div className="space-y-3 rounded-xl border border-slate-200 bg-slate-50 p-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="text-sm font-medium text-slate-700">
              Network name (SSID)
              <input
                type="text"
                value={wifiSsid}
                onChange={(event) => setWifiSsid(event.target.value)}
                placeholder="MyWiFiNetwork"
                className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200"
              />
            </label>
            <label className="text-sm font-medium text-slate-700">
              Security
              <select
                value={wifiSecurity}
                onChange={(event) => setWifiSecurity(event.target.value as WifiSecurity)}
                className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
              >
                <option value="WPA">WPA/WPA2</option>
                <option value="WEP">WEP</option>
                <option value="nopass">Open (no password)</option>
              </select>
            </label>
          </div>

          {wifiSecurity !== "nopass" && (
            <label className="block text-sm font-medium text-slate-700">
              Password
              <input
                type="text"
                value={wifiPassword}
                onChange={(event) => setWifiPassword(event.target.value)}
                placeholder="WiFi password"
                className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200"
              />
            </label>
          )}

          <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
            <input type="checkbox" checked={wifiHidden} onChange={(event) => setWifiHidden(event.target.checked)} />
            Hidden network
          </label>
        </div>
      )}

      {type === "vcard" && (
        <div className="grid gap-3 rounded-xl border border-slate-200 bg-slate-50 p-4 sm:grid-cols-2">
          <label className="text-sm font-medium text-slate-700 sm:col-span-2">
            Full name
            <input
              type="text"
              value={vcardName}
              onChange={(event) => setVcardName(event.target.value)}
              placeholder="Jane Doe"
              className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200"
            />
          </label>
          <label className="text-sm font-medium text-slate-700">
            Organization
            <input
              type="text"
              value={vcardOrg}
              onChange={(event) => setVcardOrg(event.target.value)}
              placeholder="Acme Inc."
              className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200"
            />
          </label>
          <label className="text-sm font-medium text-slate-700">
            Phone
            <input
              type="tel"
              value={vcardPhone}
              onChange={(event) => setVcardPhone(event.target.value)}
              placeholder="+1 555 123 4567"
              className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200"
            />
          </label>
          <label className="text-sm font-medium text-slate-700">
            Email
            <input
              type="email"
              value={vcardEmail}
              onChange={(event) => setVcardEmail(event.target.value)}
              placeholder="jane@example.com"
              className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200"
            />
          </label>
          <label className="text-sm font-medium text-slate-700">
            Website
            <input
              type="url"
              value={vcardUrl}
              onChange={(event) => setVcardUrl(event.target.value)}
              placeholder="https://example.com"
              className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200"
            />
          </label>
        </div>
      )}

      <div className="grid gap-4 md:grid-cols-2">
        <label className="text-sm font-medium text-slate-700">
          QR size
          <input
            type="range"
            min="120"
            max="480"
            step="10"
            value={size}
            onChange={(event) => setSize(Number(event.target.value))}
            className="mt-2 w-full"
          />
          <span className="text-xs text-slate-500">{size}px</span>
        </label>

        <label className="text-sm font-medium text-slate-700">
          Error correction
          <select
            value={errorCorrection}
            onChange={(event) => setErrorCorrection(event.target.value as ErrorCorrection)}
            className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
          >
            <option value="L">Low (7%) — smallest code</option>
            <option value="M">Medium (15%)</option>
            <option value="Q">Quartile (25%) — good with a logo</option>
            <option value="H">High (30%) — best for print or a logo</option>
          </select>
        </label>

        <label className="text-sm font-medium text-slate-700">
          Foreground color
          <input
            type="color"
            value={fgColor}
            onChange={(event) => setFgColor(event.target.value)}
            className="mt-2 h-10 w-full rounded-lg border border-slate-300"
          />
        </label>

        <label className="text-sm font-medium text-slate-700">
          Background color
          <input
            type="color"
            value={bgColor}
            onChange={(event) => setBgColor(event.target.value)}
            className="mt-2 h-10 w-full rounded-lg border border-slate-300"
          />
        </label>

        <div className="text-sm font-medium text-slate-700 md:col-span-2">
          Optional logo overlay
          <div className="mt-2 flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => logoFileInputRef.current?.click()}
              className="rounded-lg bg-slate-900 px-3 py-2 text-xs font-semibold text-white hover:bg-slate-800"
            >
              {logoName ? "Change logo" : "Upload logo"}
            </button>
            {logoName && (
              <>
                <span className="text-xs text-slate-600">{logoName}</span>
                <button
                  type="button"
                  onClick={removeLogo}
                  className="text-xs font-semibold text-rose-600 hover:underline"
                >
                  Remove
                </button>
              </>
            )}
            <input
              ref={logoFileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file) handleLogoFile(file);
                event.target.value = "";
              }}
            />
          </div>
          <p className="mt-1 text-xs text-slate-500">
            Uploaded locally, not fetched from a URL. A logo covers the center, so higher error correction helps it stay scannable.
          </p>
        </div>
      </div>

      {lowContrast && (
        <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          The foreground and background colors are close in brightness ({contrast.toFixed(1)}:1 contrast). This QR code may not scan reliably.
        </p>
      )}
      {logoRiskyWithCorrection && (
        <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          A logo with Low error correction often fails to scan. Quartile or High is safer.
        </p>
      )}
      {error && (
        <p role="alert" className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
          {error}
        </p>
      )}

      <div className="flex flex-wrap gap-3">
        <button
          type="button"
          onClick={() => void download("png")}
          disabled={!payload || isRendering}
          className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-slate-800 disabled:cursor-not-allowed disabled:bg-slate-300"
        >
          Download PNG
        </button>
        <button
          type="button"
          onClick={() => void download("svg")}
          disabled={!payload || isRendering}
          className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-slate-300"
        >
          Download SVG
        </button>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 text-center">
        <p aria-live="polite" className="text-sm text-slate-600">
          {isRendering ? "Rendering…" : status}
        </p>
        {previewUrl && (
          <NextImage
            src={previewUrl}
            alt="Generated QR code preview"
            width={300}
            height={300}
            unoptimized
            className="mx-auto mt-4 rounded-xl bg-white p-3"
          />
        )}
      </div>
    </div>
  );
}

function placeholderStatus(type: ContentType) {
  switch (type) {
    case "wifi":
      return "Enter a network name to generate a WiFi QR code.";
    case "vcard":
      return "Enter at least a name, phone, or email to generate a vCard QR code.";
    default:
      return "Enter some content to generate a QR code.";
  }
}